
import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Mail, Phone, Building } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { motion } from "framer-motion";
import { getAvatarColor, formatPhoneNumber } from "@/lib/utils";

const statusColors = {
  new: "bg-slate-100 text-slate-700",
  contacted: "bg-purple-100 text-purple-700",
  qualifying: "bg-amber-100 text-amber-700",
  converted: "bg-emerald-100 text-emerald-700",
  lost: "bg-slate-100 text-slate-700"
};

export default function LeadList({ 
  leads, 
  isLoading, 
  onViewLead,
  selectedLeadIds = [],
  onToggleSelect
}) {
  if (isLoading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array(6).fill(0).map((_, i) => (
          <Card key={i} className="p-6">
            <Skeleton className="h-12 w-12 rounded-full mb-4" />
            <Skeleton className="h-5 w-3/4 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {leads.map((lead, index) => {
        const isSelected = selectedLeadIds.includes(lead.id);
        
        return (
          <motion.div
            key={lead.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="h-full"
          >
            <Link 
              to={`/lead-detail?id=${lead.id}`}
              onClick={(e) => {
                // Don't navigate if clicking checkbox
                if (e.target.closest('[role="checkbox"]')) return;
                
                // Se não for Ctrl/Cmd+Click, previne navegação padrão e usa SPA
                if (!e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                  onViewLead(lead.id);
                }
              }}
              className="block h-full"
            >
              <Card 
                className={`p-6 h-full flex flex-col hover:shadow-xl hover:scale-105 transition-all duration-300 border-slate-200 bg-white cursor-pointer ${
                  isSelected ? "ring-2 ring-primary border-primary" : ""
                }`}
              >
              <div className="flex items-start justify-between mb-4">
                {/* Checkbox */}
                {onToggleSelect && (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(lead.id)}
                    />
                  </div>
                )}
              <div className={`w-12 h-12 bg-gradient-to-br ${getAvatarColor(lead.first_name)} rounded-full flex items-center justify-center shadow-sm`}>
                <span className="text-white font-bold text-lg">
                  {lead.first_name.charAt(0)}{lead.last_name.charAt(0)}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <Badge className={`${statusColors[lead.status]} border-0 font-medium pointer-events-none`}>
                  {lead.status}
                </Badge>
                {lead.market && (
                  <Badge 
                    variant="outline" 
                    className={`border-0 font-medium pointer-events-none text-xs ${
                      lead.market === 'US' 
                        ? 'bg-blue-100 text-blue-700' 
                        : lead.market === 'BR' 
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {lead.market}
                  </Badge>
                )}
              </div>
            </div>

            <h3 className="text-lg font-bold text-slate-900 mb-3">
              {lead.first_name} {lead.last_name}
            </h3>
            
            <div className="space-y-2 flex-1">
              {lead.email && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{lead.email}</span>
                </div>
              )}
              {lead.phone && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>{formatPhoneNumber(lead.phone)}</span>
                </div>
              )}
              {lead.company && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Building className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{lead.company}</span>
                </div>
              )}
            </div>
          </Card>
        </Link>
        </motion.div>
        );
      })}
    </div>
  );
}
